"use client";
import {
  BetaSegmentedControl,
  BetaSegmentedControlOption,
  Checkbox,
  Spinner,
  TextInput,
  VisuallyHidden,
} from "@zapier/design-system";
import {
  Autocomplete,
  Header,
  Input,
  ListBox,
  ListBoxItem,
  ListBoxSection,
  type Selection,
  Text,
  TextField,
} from "react-aria-components";
import { useEffect, useRef, useState, useTransition } from "react";
import styles from "./FilterSelectMenu.module.css";

type FilterOperator = "any" | "all" | "none";

export interface FilterMenuItem {
  id: string;
  name: string;
  renderIcon?: () => React.ReactNode;
  description?: string;
}

interface Props {
  label: string;
  selectedIds: string[];
  /**
   * In `single` mode there are no checkboxes, the selected/available split
   * is collapsed, and clicking a row replaces the current value via
   * `onToggle` ("set to this id").
   * @default "multi"
   */
  selectionMode?: "multi" | "single";
  operator: FilterOperator;
  /**
   * Operators the consumer supports. The selector is hidden when fewer
   * than two are provided. Rendered in the order given.
   * @default ["any", "all", "none"]
   */
  availableOperators?: FilterOperator[];
  /** Per-operator label overrides; missing entries fall back to "Any" / "All" / "None". */
  operatorLabels?: Partial<Record<FilterOperator, string>>;
  items: FilterMenuItem[];
  /** Custom header for the "available" section when needed. */
  availableSectionHeader?: string;
  search: string;
  showSearch?: boolean;
  isLoading?: boolean;
  isFetching?: boolean;
  /** Placeholder + aria-label for the search input. @default "Search" */
  searchPlaceholder?: string;
  /** Empty-state text. @default "No results" */
  noResultsText?: string;
  /** Polite SR announcement builder, e.g. `(n) => "5 apps found"`. Opt-in. */
  resultsAnnouncement?: (count: number) => string;
  /** aria-label for the operator selector. @default "Match operator" */
  matchAriaLabel?: string;
  onOperatorChange?: (operator: FilterOperator) => void;
  /**
   * Called when an item's selection state toggles. Forwards the menu's
   * current optimistic operator so the consumer can apply operator + value
   * atomically — the operator can't be persisted alone until at least one
   * value exists.
   */
  onToggle: (id: string, pendingOperator?: FilterOperator) => void;
  onSearchChange?: (value: string) => void;
  renderEmpty?: () => React.ReactNode;
}

const DEFAULT_OPERATORS: FilterOperator[] = ["any", "all", "none"];

const DEFAULT_OPERATOR_LABELS: Record<FilterOperator, string> = {
  any: "Any",
  all: "All",
  none: "None",
};

/**
 * Searchable filter menu with optional operator selector and "sticky"
 * selected items: rows selected when the menu opened stay pinned at the
 * top until the menu closes, so accidental unchecks don't make rows jump.
 *
 * Multi-select uses a React Aria `Autocomplete` + `ListBox` so the search
 * input keeps focus while Up/Down arrows navigate options (virtual focus,
 * single tab stop). Single-select uses `ListBox` with roving focus so
 * Up/Down scroll the option list instead of the page behind the popover.
 */
export function FilterSelectMenu({
  label,
  selectedIds,
  selectionMode = "multi",
  operator,
  availableOperators = DEFAULT_OPERATORS,
  operatorLabels,
  items,
  availableSectionHeader,
  search,
  showSearch = true,
  isLoading,
  isFetching,
  searchPlaceholder = "Search",
  noResultsText = "No results",
  resultsAnnouncement,
  matchAriaLabel = "Match operator",
  onOperatorChange,
  onToggle,
  onSearchChange,
  renderEmpty,
}: Props) {
  const showOperator = availableOperators.length > 1;
  const hasSearch = search.trim().length > 0;
  const showFetchingSpinner =
    isFetching === true && !(isLoading === true && items.length === 0);

  // Polite SR announcement for "N results" — fires only when the count
  // changes and the menu has settled.
  const [resultsAnnouncementText, setResultsAnnouncementText] = useState("");
  const lastAnnouncedCountReference = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!resultsAnnouncement) return;
    if (isLoading || isFetching) return;
    if (lastAnnouncedCountReference.current === items.length) return;
    lastAnnouncedCountReference.current = items.length;
    setResultsAnnouncementText(resultsAnnouncement(items.length));
  }, [items.length, isLoading, isFetching, resultsAnnouncement]);

  // Operator state while the menu is open — can't persist to the URL until
  // at least one value is selected.
  const [pendingOperator, setPendingOperator] = useState(operator);
  const userTouchedOperatorReference = useRef(false);
  useEffect(() => {
    if (!userTouchedOperatorReference.current) {
      setPendingOperator(operator);
    }
  }, [operator]);
  const startOperatorTransition = useTransition()[1];

  /** Snapshot of selectedIds at mount time — fixed for the menu's lifetime so rows don't jump mid-interaction. */
  const stickySelectedReference = useRef<Set<string>>(new Set(selectedIds));
  const stickySelectedIds = stickySelectedReference.current;

  const byId = new Map(items.map((item) => [item.id, item]));
  const selectedItems = [...stickySelectedIds]
    .map((id) => byId.get(id))
    .filter((item): item is FilterMenuItem => item !== undefined);
  const unselectedItems = items.filter(
    (item) => !stickySelectedIds.has(item.id),
  );
  const selectedIdSet = new Set(selectedIds);

  const operatorLabel = (op: FilterOperator): string => {
    return operatorLabels?.[op] ?? DEFAULT_OPERATOR_LABELS[op];
  };

  const showHeader = (showSearch && !!onSearchChange) || showOperator;

  // Skeleton rows mirror real-item dimensions to stop height jump.
  const skeleton = (
    <div
      className={styles.skeletonGroup}
      aria-hidden={true}
      data-testid="filter-menu-skeleton"
    >
      {Array.from({ length: 5 }, (_, index) => (
        <div key={index} className={styles.skeletonRow}>
          <div className={styles.skeletonIcon} />
          <div className={styles.skeletonText} />
        </div>
      ))}
    </div>
  );

  const emptyState = (
    <div className={styles.empty}>
      {renderEmpty ? renderEmpty() : noResultsText}
    </div>
  );

  const announcement = resultsAnnouncement && (
    <VisuallyHidden>
      <div role="status" aria-live="polite" aria-atomic="true">
        {resultsAnnouncementText}
      </div>
    </VisuallyHidden>
  );

  const operatorControl = showOperator && onOperatorChange && (
    <div className={styles.operatorRow}>
      <BetaSegmentedControl
        size="medium"
        aria-label={matchAriaLabel}
        selectedKey={
          availableOperators.includes(pendingOperator)
            ? pendingOperator
            : availableOperators[0]
        }
        onSelectionChange={(key) => {
          if (!key) return;
          const next = key as FilterOperator;
          userTouchedOperatorReference.current = true;
          setPendingOperator(next);
          // Only echo to URL when a value exists; pendingOperator
          // hands over via `onToggle` when the first value lands.
          if (selectedIds.length > 0) {
            startOperatorTransition(() => {
              onOperatorChange(next);
            });
          }
        }}
      >
        {availableOperators.map((op) => (
          <BetaSegmentedControlOption key={op} id={op}>
            {operatorLabel(op)}
          </BetaSegmentedControlOption>
        ))}
      </BetaSegmentedControl>
    </div>
  );

  const fetchingSpinner = showFetchingSpinner && (
    <span
      className={styles.fetchingSpinner}
      data-testid="filter-menu-fetching-spinner"
    >
      <Spinner aria-label="Loading filter options" size="small" />
    </span>
  );

  const renderSingleOption = (item: FilterMenuItem) => {
    return (
      <ListBoxItem
        key={item.id}
        id={item.id}
        textValue={item.name}
        className={styles.item}
      >
        {item.renderIcon && (
          <span className={styles.iconSlot}>{item.renderIcon()}</span>
        )}
        <span className={styles.itemLabel}>{item.name}</span>
        {item.description && (
          <Text slot="description" className={styles.itemDescription}>
            {item.description}
          </Text>
        )}
      </ListBoxItem>
    );
  };

  const useAutocomplete = showSearch && !!onSearchChange;
  const initialSingleSelectionReference = useRef(selectedIds[0]);

  if (selectionMode === "single") {
    const singleEmptyListPanel = items.length === 0 && (
      <div
        className={styles.list}
        role="region"
        aria-label={label}
        aria-busy={isLoading || undefined}
      >
        {isLoading ? skeleton : emptyState}
      </div>
    );

    const singleListBoxPanel = items.length > 0 && (
      <ListBox
        className={styles.list}
        aria-label={label}
        // eslint-disable-next-line jsx-a11y/no-autofocus -- focus first option when chip menu opens for arrow-key nav
        autoFocus="first"
        escapeKeyBehavior="none"
        selectionMode="single"
        shouldFocusWrap={true}
        disallowEmptySelection={false}
        defaultSelectedKeys={
          selectedIds.length > 0 ? new Set(selectedIds) : undefined
        }
        onSelectionChange={(keys) => {
          if (keys === "all") return;
          if (keys.size === 0) {
            const current =
              initialSingleSelectionReference.current ?? selectedIds[0];
            if (current !== undefined) onToggle(current, pendingOperator);
            return;
          }
          const id = [...keys][0];
          if (id !== undefined) {
            initialSingleSelectionReference.current = String(id);
            onToggle(String(id), pendingOperator);
          }
        }}
        renderEmptyState={() => emptyState}
      >
        {items.map((item) => renderSingleOption(item))}
      </ListBox>
    );

    const singleHeader = showHeader && (
      <>
        <div className={styles.header}>
          {showSearch && onSearchChange && (
            <div className={styles.searchWrapper}>
              {useAutocomplete && (
                <TextField
                  aria-label={searchPlaceholder}
                  className={styles.searchField}
                >
                  <Input
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus={true}
                    className={styles.searchInput}
                    placeholder={searchPlaceholder}
                  />
                </TextField>
              )}
              {!useAutocomplete && (
                <TextInput
                  aria-label={searchPlaceholder}
                  placeholder={searchPlaceholder}
                  value={search}
                  onChange={(event) => onSearchChange(event.target.value)}
                  isFullWidth={true}
                />
              )}
              {fetchingSpinner}
            </div>
          )}
          {operatorControl}
        </div>
        <div className={styles.headerDivider} />
      </>
    );

    return (
      <div className={styles.root}>
        {announcement}
        {useAutocomplete && onSearchChange && (
          <Autocomplete inputValue={search} onInputChange={onSearchChange}>
            {singleHeader}
            {singleEmptyListPanel}
            {singleListBoxPanel}
          </Autocomplete>
        )}
        {(!useAutocomplete || !onSearchChange) && (
          <>
            {singleHeader}
            {singleEmptyListPanel}
            {singleListBoxPanel}
          </>
        )}
      </div>
    );
  }

  const renderOption = (item: FilterMenuItem) => {
    return (
      <ListBoxItem
        key={item.id}
        id={item.id}
        textValue={item.name}
        className={styles.item}
      >
        {({ isSelected }) => (
          <>
            {/* Presentational checkbox — the option itself owns selection via
                its `aria-selected`, so the checkbox is hidden from assistive
                tech (wrapper `aria-hidden`) and made non-focusable via
                `isDisabled` (a disabled input can't be focused, so it can't be
                a focusable descendant of an `aria-hidden` region). Disabled
                styling is overridden in CSS so it still reads as active. */}
            <span className={styles.checkbox} aria-hidden={true}>
              <Checkbox isDisabled={true} isSelected={isSelected} />
            </span>
            {item.renderIcon && (
              <span className={styles.iconSlot}>{item.renderIcon()}</span>
            )}
            <span className={styles.itemLabel}>{item.name}</span>
            {item.description && (
              <Text slot="description" className={styles.itemDescription}>
                {item.description}
              </Text>
            )}
          </>
        )}
      </ListBoxItem>
    );
  };

  // Sticky selected rows pin to the top until the menu closes; while
  // searching we show the raw results in order. When an available-section
  // header is supplied, the unselected rows move into a labelled section.
  let listChildren: React.ReactNode;
  if (hasSearch) {
    listChildren = items.map((item) => renderOption(item));
  } else if (availableSectionHeader && unselectedItems.length > 0) {
    listChildren = (
      <>
        {selectedItems.map((item) => renderOption(item))}
        <ListBoxSection id="__available">
          <Header className={styles.sectionHeader}>
            {availableSectionHeader}
          </Header>
          {unselectedItems.map((item) => renderOption(item))}
        </ListBoxSection>
      </>
    );
  } else {
    listChildren = [...selectedItems, ...unselectedItems].map((item) =>
      renderOption(item),
    );
  }

  const handleSelectionChange = (keys: Selection) => {
    // No select-all affordance is rendered, so `keys` is always a Set.
    if (keys === "all") return;
    const nextIds = new Set<string>();
    for (const key of keys) nextIds.add(String(key));

    const changed = new Set<string>();
    for (const id of nextIds) {
      if (!selectedIdSet.has(id)) changed.add(id);
    }
    for (const id of selectedIdSet) {
      if (!nextIds.has(id)) changed.add(id);
    }

    // Only toggle ids present in the current (possibly filtered) collection,
    // so previously-selected owners that aren't in the visible results are
    // never inadvertently unchecked.
    for (const id of changed) {
      if (byId.has(id)) onToggle(id, pendingOperator);
    }
  };

  const emptyListPanel = items.length === 0 && (
    <div
      className={styles.list}
      role="region"
      aria-label={label}
      aria-busy={isLoading || undefined}
    >
      {isLoading ? skeleton : emptyState}
    </div>
  );

  const listBoxPanel = items.length > 0 && (
    <ListBox
      className={styles.list}
      aria-label={label}
      escapeKeyBehavior="none"
      selectionMode="multiple"
      selectionBehavior="toggle"
      shouldFocusWrap={true}
      disallowEmptySelection={false}
      selectedKeys={new Set(selectedIds)}
      onSelectionChange={handleSelectionChange}
      renderEmptyState={() => emptyState}
    >
      {listChildren}
    </ListBox>
  );

  const header = showHeader && (
    <>
      <div className={styles.header}>
        {useAutocomplete && (
          <div className={styles.searchWrapper}>
            <TextField
              aria-label={searchPlaceholder}
              className={styles.searchField}
            >
              <Input
                // TODO: Figure out how to auto-focus the input when the popover is opened from the Filterrs Button -> Owner
                // Right now, the input is only autofocused when the popover is opened from an "Owner is" chip in the FilterBar.
                // Autofocus is desirable here so that users can begin typing immediately after opening the popover
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={true}
                className={styles.searchInput}
                placeholder={searchPlaceholder}
              />
            </TextField>
            {fetchingSpinner}
          </div>
        )}
        {operatorControl}
      </div>
      <div className={styles.headerDivider} />
    </>
  );

  return (
    <div className={styles.root}>
      {announcement}
      {useAutocomplete && (
        <Autocomplete inputValue={search} onInputChange={onSearchChange}>
          {header}
          {emptyListPanel}
          {listBoxPanel}
        </Autocomplete>
      )}
      {!useAutocomplete && (
        <>
          {header}
          {emptyListPanel}
          {listBoxPanel}
        </>
      )}
    </div>
  );
}
